/**
 * Badge Component
 * Small status and count indicators
 */

import { ComponentProps } from 'react'
import clsx from 'clsx'

interface BadgeProps extends ComponentProps<'span'> {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'gradient'
  size?: 'sm' | 'md' | 'lg'
  dot?: boolean
}

export default function Badge({
  variant = 'default',
  size = 'md',
  dot = false,
  children,
  className = '',
  ...props
}: BadgeProps) {
  const variantStyles = {
    default: 'bg-white/10 text-gray-300 border-white/20',
    success: 'bg-green-500/20 text-green-400 border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    gradient: 'bg-gradient-to-r from-[#667EEA] to-[#764BA2] text-white border-0',
  }
  
  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  }
  
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 font-medium rounded-full border backdrop-blur-sm',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {dot && (
        <span className={clsx(
          'w-1.5 h-1.5 rounded-full',
          variant === 'default' && 'bg-gray-400',
          variant === 'success' && 'bg-green-400 animate-pulse-soft',
          variant === 'warning' && 'bg-yellow-400',
          variant === 'error' && 'bg-red-400',
          variant === 'info' && 'bg-blue-400',
          variant === 'gradient' && 'bg-white',
        )} />
      )}
      {children}
    </span>
  )
}
